import type RpcDataPagination     from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort           from '@/core/services/Rpc/RpcDataSort';
import Core                       from '@/core/Core';
import PaymentRequest             from '@/domains/PaymentRequests/PaymentRequest';
import PaymentRequestPosition     from '@/domains/PaymentRequests/PaymentRequestPosition';
import PaymentRequestsDAO         from '@/domains/PaymentRequests/PaymentRequestsDAO';
import EntityPointer              from '@/domains/EntityPointer/EntityPointer';
import PaymentReasonType          from "@/domains/PaymentRequests/PaymentReasonType";
import PaymentPriorityType        from "@/domains/PaymentRequests/PaymentPriorityType";
import Dictionary                 from "@/core/types/Dictionary";
import PaymentReasonBasis         from "@/domains/PaymentRequests/PaymentReasonBasis";
import ContextMenuItem            from '@/lib/components/VContextMenu/ContextMenuItem';

/**
 * Сервис для работы с заявками на платёж
 */
class PaymentRequestsService {

	// ============================================================
	// Справочники
	// ============================================================

	/**
	 * Кеш приоритетов платежа. Наполняется из Dictionaries общим эндпоинтом.
	 * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
	 */
	static paymentPriorityTypes = reactive(new Dictionary<number, PaymentPriorityType>());

	/**
	 * Кеш оснований платежа. Наполняется из Dictionaries общим эндпоинтом.
	 * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
	 */
	static paymentReasonBases = reactive(new Dictionary<number, PaymentReasonBasis>());

	static get reasonTypes(): PaymentReasonType[] {
		const all = PaymentReasonType.all();

		// Для Намангана — только invoice и contractInvoice
		if (Core.currentPartner?.id === 497) {
			return all.filter(type => type.id === 'invoice' || type.id === 'contractInvoice');
		}

		return all;
	}

	static exportStatusList = [
		{ id: false, name: 'Ждёт отправки' },
		{ id: true, name: 'Отправлено' },
	];

	/**
	 * Опции селекта "Статус" в реестре платежей. Имена флагов совпадают
	 * с filter-identity колонки и параметрами getList на бэке
	 */
	static isPaidStatusList = [
		{ id: 'paid',         name: 'Оплачено',    isPaid: true       },
		{ id: 'unpaid',       name: 'Не оплачено', isPaid: false      },
		{ id: 'canceled',     name: 'Отменено',    isCanceled: true   },
		{ id: 'isProcessing', name: 'На оплате',   isProcessing: true },
	];

	// ============================================================
	// CRUD
	// ============================================================

	/**
	 * Загрузить одну заявку на платёж по ID
	 */
	static async get(id: number): Promise<PaymentRequest | null> {
		const response = await PaymentRequestsDAO.get(id);

		if (!response.data) return null;

		return PaymentRequest.fromServer(response.data);
	}

	/**
	 * Загрузить список заявок на платёж с пагинацией.
	 * Используется в реестре через DataGridLoader.
	 */
	static async getListPaginated(
    params?: Parameters<typeof PaymentRequestsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: PaymentRequest[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await PaymentRequestsDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, PaymentRequest.fromServer),
			pagination: response.meta.pagination,
		};
	}

	/**
	 * Загрузить список заявок на платёж.
	 * Для использования вне реестра (диалоги, автокомплиты).
	 */
	static async getList(
    params?: Parameters<typeof PaymentRequestsDAO.getList>[0]
  ): Promise<PaymentRequest[]> {
		return (await this.getListPaginated(params)).data;
	}

	/**
	 * Создать заявку на платёж
	 */
	static async create(paymentRequest: PaymentRequest): Promise<boolean> {
		const errors = paymentRequest.validate();
		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return false;
		}

		const response = await PaymentRequestsDAO.add(paymentRequest);

		if (response.ok) {
			Core.showNotification.success('Платёж успешно создан');
		}

		return response.ok;
	}

	/**
	 * Обновить заявку на платёж
	 */
	static async update(paymentRequest: PaymentRequest): Promise<boolean> {
		const errors = paymentRequest.validate();
		if (errors.length > 0) {
			Core.showNotification.error(errors.join('\n'));
			return false;
		}

		const response = await PaymentRequestsDAO.update(paymentRequest);

		if (response.ok) {
			Core.showNotification.success('Платёж обновлён');
		}

		return response.ok;
	}

	/**
	 * Отменить заявку на платёж с подтверждением.
	 * Возвращает true если отмена произошла — вызывающий сам решает что делать
	 * (обычно refresh лоадера регистра). Сигнатура под :context-menu грида,
	 * по аналогии с deleteWithConfirm в эталонном AssembliesService.
	 */
	static async cancelWithConfirm(id: number): Promise<boolean> {
		const confirmed = await Core.confirm('Отменить платёж?');

		if (confirmed) {
			return await this.cancel(id);
		} else {
			return false;
		}
	}

	/**
	 * Отменить заявку на платёж
	 */
	static async cancel(id: number): Promise<boolean> {
		const response = await PaymentRequestsDAO.cancel(id);

		if (response.ok) {
			Core.showNotification.success('Платёж отменён');
		}

		return response.ok;
	}

	/**
	 * Обновить приоритет заявки
	 */
	static async updatePriority(id: number, priorityType: { id: number } | null): Promise<boolean> {
		const response = await PaymentRequestsDAO.updateStatus({
			id,
			priorityType: priorityType || undefined,
		});

		if (response.ok) {
			Core.showNotification.success('Приоритет обновлён');
		}

		return response.ok;
	}

	// ============================================================
	// UI Helpers
	// ============================================================

	/**
	 * Открывает диалог истории платежа
	 */
	static showHistory(paymentRequestId: number): void {
		Core.dialogs.FeedDialog.show({
			entityTypeName: 'paymentRequest',
			entityId: paymentRequestId,
			showAddComment: false,
		});
	}

	/**
	 * Открывает диалог файлов платежа
	 */
	static showFilesDialog(paymentRequest: PaymentRequest): void {
		if (!paymentRequest.id) return;

		Core.dialogs.FilesDialog.showAsPromise({
				title: `Платеж №${Core.utils.string.formatWithDelimiter(paymentRequest.id)}`,
				fileEntities: [new EntityPointer('paymentRequest', paymentRequest.id, `Платёж №${paymentRequest.id}`)],
				maxOneFile: true,
			},
		);
	}

	/**
	 * Платежи по позиции заказа — для попапа в реестрах.
	 * Каждый элемент несёт позицию платежа и полностью гидрированную заявку,
	 * поэтому paymentRequest собирается отдельно через fromPaymentRequest.
	 */
	static async getPositionPayments(orderPosition: { id: number }): Promise<PaymentRequestPosition[]> {
		const response = await PaymentRequestsDAO.getPositionList(orderPosition);

		return Core.utils.isNotEmptyArray(response.data)
			? response.data.map((item: any) => {
				const position = PaymentRequestPosition.fromServer(item);
				position.paymentRequest = PaymentRequest.fromServer(item.paymentRequest);
				return position;
			})
			: [];
	}

	/**
	 * Открывает карточку платежа по id. Карточка сама догружает данные.
	 */
	static showCard(id: number): void {
		Core.dialogs.PaymentRequestCard.show({ id });
	}

	// ============================================================
	// Контекстное меню
	// ============================================================

	/**
	 * Пункты контекстного меню платежа для грида реестра и карточки.
	 * Передаётся в VDataGridV2 / VDialogV3 пропом :context-menu — они сами
	 * рисуют меню и обновляют список после действия (если refreshAfter !== false).
	 *
	 * Геттер, а не статическое свойство: «Отменить» зависит от
	 * paymentRequest.canBeCanceled, вычисляемого на конкретной заявке.
	 *
	 * callback возвращает промис — грид дождётся завершения перед refresh.
	 *   - cancelWithConfirm → Promise<boolean>
	 *   - showCard → void; карточка сама эмитит Updated, реестр рефрешится
	 *     по своей подписке → refreshAfter: false
	 *   - showHistory / showFilesDialog → void, список не меняют → refreshAfter: false
	 *
	 * «Изменить приоритет» здесь намеренно отсутствует: в реестре приоритет
	 * правится через inline-попап (своя колонка), это локальная механика реестра,
	 * а не действие над загруженной сущностью.
	 */
	static get contextMenu(): ContextMenuItem[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(paymentRequest: PaymentRequest) => { this.showCard(paymentRequest.id); },
				() => null,   // reasonDisabled
				false,  // hidden
				false,  // refreshAfter — карточка сама эмитит Updated
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'История платежа',
				'history',
				(paymentRequest: PaymentRequest) => { this.showHistory(paymentRequest.id); },
				() => null,   // reasonDisabled
				false,  // hidden
				false,  // refreshAfter
			),
			new ContextMenuItem(
				'Файлы',
				'file_present',
				(paymentRequest: PaymentRequest) => { this.showFilesDialog(paymentRequest); },
				() => null,   // reasonDisabled
				false,  // hidden
				false,  // refreshAfter
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Отменить',
				'delete',
				(paymentRequest: PaymentRequest) => { return this.cancelWithConfirm(paymentRequest.id); },
				(paymentRequest: PaymentRequest) => paymentRequest.canBeCanceled.allowed ? null : paymentRequest.canBeCanceled.reason,
			),
		];
	}
}

export default PaymentRequestsService;
